import { useRef } from 'react'
import type { PenToolOverlayProps } from '../types/pen-tool-overlay-props'

type PenPropRefInput = Required<
  Pick<
    PenToolOverlayProps,
    | 'bleedWidth'
    | 'bleedHeight'
    | 'scale'
    | 'defaultPointType'
    | 'onPathCompleted'
    | 'skipShapeExitCheck'
    | 'snapToGrid'
    | 'smartGuidesEnabled'
    | 'snapGuideDistance'
  >
> &
  Pick<
    PenToolOverlayProps,
    | 'onCancel'
    | 'canvasScrollerRef'
    | 'onExitPenTool'
    | 'onDrawingStart'
    | 'gridSnapStep'
    | 'gridSnapLiveRects'
    | 'getShapeBoundsForSmartGuides'
    | 'onSmartGuidesChange'
  >

/**
 * Refs to latest prop values — keep handlers stable without stale closures. Each `.current` is
 * re-synced during render, so the window listeners (registered once on mount) always read the
 * current props.
 */
export function usePenPropRefs(props: PenPropRefInput) {
  const scaleRef = useRef(props.scale)

  scaleRef.current = props.scale
  const defaultPointTypeRef = useRef(props.defaultPointType)

  defaultPointTypeRef.current = props.defaultPointType
  const bleedWidthRef = useRef(props.bleedWidth)

  bleedWidthRef.current = props.bleedWidth
  const bleedHeightRef = useRef(props.bleedHeight)

  bleedHeightRef.current = props.bleedHeight
  const onPathCompletedRef = useRef(props.onPathCompleted)

  onPathCompletedRef.current = props.onPathCompleted
  const onCancelRef = useRef(props.onCancel)

  onCancelRef.current = props.onCancel
  const canvasScrollerRefRef = useRef(props.canvasScrollerRef)

  canvasScrollerRefRef.current = props.canvasScrollerRef
  const onExitPenToolRef = useRef(props.onExitPenTool)

  onExitPenToolRef.current = props.onExitPenTool
  const skipShapeExitCheckRef = useRef(props.skipShapeExitCheck)

  skipShapeExitCheckRef.current = props.skipShapeExitCheck
  const onDrawingStartRef = useRef(props.onDrawingStart)

  onDrawingStartRef.current = props.onDrawingStart
  const snapToGridRef = useRef(props.snapToGrid)

  snapToGridRef.current = props.snapToGrid
  const gridSnapStepRef = useRef(props.gridSnapStep)

  gridSnapStepRef.current = props.gridSnapStep
  const gridSnapLiveRectsRef = useRef(props.gridSnapLiveRects)

  gridSnapLiveRectsRef.current = props.gridSnapLiveRects
  const smartGuidesEnabledRef = useRef(props.smartGuidesEnabled)

  smartGuidesEnabledRef.current = props.smartGuidesEnabled
  const snapGuideDistanceRef = useRef(props.snapGuideDistance)

  snapGuideDistanceRef.current = props.snapGuideDistance
  const getShapeBoundsForSmartGuidesRef = useRef(props.getShapeBoundsForSmartGuides)

  getShapeBoundsForSmartGuidesRef.current = props.getShapeBoundsForSmartGuides
  const onSmartGuidesChangeRef = useRef(props.onSmartGuidesChange)

  onSmartGuidesChangeRef.current = props.onSmartGuidesChange

  return {
    scaleRef,
    defaultPointTypeRef,
    bleedWidthRef,
    bleedHeightRef,
    onPathCompletedRef,
    onCancelRef,
    canvasScrollerRefRef,
    onExitPenToolRef,
    skipShapeExitCheckRef,
    onDrawingStartRef,
    snapToGridRef,
    gridSnapStepRef,
    gridSnapLiveRectsRef,
    smartGuidesEnabledRef,
    snapGuideDistanceRef,
    getShapeBoundsForSmartGuidesRef,
    onSmartGuidesChangeRef,
  }
}

export type PenPropRefs = ReturnType<typeof usePenPropRefs>
